import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  InputGroup, 
  Button, 
  Badge, 
  Spinner,
  Dropdown,
  Modal,
  Tabs,
  Tab,
  ProgressBar,
  Alert
} from 'react-bootstrap';
import { 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaCheck, 
  FaTimes, 
  FaEdit, 
  FaArrowRight,
  FaExclamationTriangle,
  FaExternalLinkAlt
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { backofficeApi ,commonApi} from '../../services/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate} @ ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

const BackofficeLeads = () => {
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedDsoId, setSearchedDsoId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [DsoFilter, setDsoFilter] = useState([]);  
  const [statusFilter, setStatusFilter] = useState('All');  
  const [productFilter, setProductFilter] = useState('All');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadNotes, setLeadNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [savedNotes, setSavedNotes] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(20); // you can make this configurable
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchStatuses();
    fetchDsoList();
  }, []);

  const fetchStatuses = async () => {
    try {
      const res = await commonApi.getStatusList();
      if (res.data && Array.isArray(res.data)) {
        setStatuses(res.data);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const fetchDsoList = async () => {
    try {
      const res = await commonApi.getDsoList();
      if (res.data && Array.isArray(res.data)) {
        setDsoFilter(res.data);
      }
    } catch (error) {
      console.error('Error fetching dso list:', error);
    }
  };
  
  
  useEffect(() => {
    document.title = 'Backoffice Applications';
    fetchLeads();
  }, [page,statusFilter, productFilter, searchTerm, searchedDsoId]);

  // Fetch leads from the API
  const fetchLeads = async () => {
    setLoading(true);
    
    try {
      // Call the new backoffice applications API endpoint
      const response = await backofficeApi.getApplications({
        skip: (page - 1) * limit,
        limit,
        status: statusFilter !== 'All' ? Number(statusFilter) : undefined,
        product_id: productFilter !== 'All' ? productFilter : undefined,
        search: searchTerm || undefined,
        searched_dso_id: searchedDsoId || undefined,
      });
      
      // Format the response data
      if (response.data && response.data.items) {
        // const formattedLeads = response.data.items.map(item => {
        //   // Format lead data
        //   return {
        //     id: item.id,
        //     name: item.lead_user?.name || 'Unknown',
        //     mobile: item.lead_user?.phone || 'N/A',
        //     product: item.product?.name || 'Unknown Product',
        //     product_id: item.product?.id,
        //     loan_amount: item.loan_amount || null,
        //     status: item.status?.name || 'Unknown',
        //     status_code: item.status?.code || 0,
        //     date: new Date(item.created_at).toISOString().split('T')[0],
        //     completion: item.completion || 50, // Default to 50% if not available
        //     agent: item.agent?.name || 'Unassigned',
        //     dso: item.dso?.name || 'Not specified'
        //   };
        // });

         let formattedLeads = [];

        // Case 1: grouped by DSO (user.id == 361)
        if (response.data.items[0]?.dso_id) {
          response.data.items.forEach(group => {
            group.items.forEach(item => {
              formattedLeads.push({
                id: item.id,
                name: item.lead_user?.name || "Unknown",
                mobile: item.lead_user?.phone || "N/A",
                product: item.product?.name || "Unknown Product",
                product_id: item.product?.id,
                loan_amount: item.loan_amount || null,
                status: item.status?.name || "Unknown",
                status_code: item.status?.code || 0,
                date: new Date(item.created_at).toISOString().split("T")[0],
                completion: item.completion || 50,
                agent: item.agent?.name || "Unassigned",
                dso: group.dso_name || "Not specified" // take from group
              });
            });
          });
        } 
        // Case 2: flat leads (other users)
        else {
          formattedLeads = response.data.items.map(item => ({
            id: item.id,
            name: item.lead_user?.name || "Unknown",
            mobile: item.lead_user?.phone || "N/A",
            product: item.product?.name || "Unknown Product",
            product_id: item.product?.id,
            loan_amount: item.loan_amount || null,
            status: item.status?.name || "Unknown",
            status_code: item.status?.code || 0,
            date: new Date(item.created_at).toISOString().split("T")[0],
            completion: item.completion || 50,
            agent: item.agent?.name || "Unassigned",
            dso: item.dso?.name || "Not specified"
          }));
        }
        
        setLeads(formattedLeads);
        setTotal(response.data.total || 0); // assuming API returns total count

      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lead details including notes and documents
  const fetchLeadDetails = async (leadId) => {
    setLoading(true);
    try {
      // Fetch complete case details including notes and documents from backend
      const detailsResponse = await backofficeApi.getCaseDetails(leadId);
      
      // Update notes
      if (detailsResponse.data && detailsResponse.data.notes) {
        setLeadNotes(detailsResponse.data.notes);
        // Also update savedNotes state for display
        setSavedNotes(prev => ({
          ...prev,
          [leadId]: detailsResponse.data.notes
        }));
      } else {
        setLeadNotes('');
      }
      
      // Update selectedLead with document URLs
      if (detailsResponse.data && detailsResponse.data.documents) {
        setSelectedLead(prevLead => {
          if (!prevLead) return null;
          
          // Create a deep clone of the lead to avoid direct state mutation
          const updatedLead = JSON.parse(JSON.stringify(prevLead));
          
          // If lead_user doesn't exist, create it
          if (!updatedLead.lead_user) {
            updatedLead.lead_user = {};
          }
          
          // Add document URLs to lead_user
          updatedLead.lead_user = {
            ...updatedLead.lead_user,
            ...detailsResponse.data.documents,
            ...(detailsResponse.data.lead_profile || {}),
            ...(detailsResponse.data.lead_user || {}),
            // Also add additional lead details if available
            ...(detailsResponse.data.lead_details || {})
          };
          
          return updatedLead;
        });
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      alert('Failed to fetch lead details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // View lead details
  const handleViewLead = async (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
    
    // Fetch additional details
    await fetchLeadDetails(lead.id);
  };

  // Filter leads by search term and status
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.mobile.includes(searchTerm) ||
      lead.agent.toLowerCase().includes(searchTerm.toLowerCase()) || lead.dso.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle numeric status codes - convert statusFilter to number for comparison
    const matchesStatus = 
      statusFilter === 'All' || 
      lead.status_code === Number(statusFilter);
    
    const matchesProduct = productFilter === 'All' || lead.product === productFilter;
    
    return matchesSearch && matchesStatus && matchesProduct;
  });

  // Render status badge
  const renderStatusBadge = (status, statusCode) => {
    let variant = "primary";
    
    switch(statusCode) {
      case 7:
        variant = "info";
        break;
      case 8:
        variant = "warning";
        break;
      case 12:
      case 13:
        variant = "success";
        break;
      case 10:
        variant = "secondary";
        break;
      case 11:
        variant = "danger";
        break;
      default:
        variant = "primary";
    }
    
    return <Badge bg={variant}>{status}</Badge>;
  };

  
  // Handle Apply to Bank action
  const handleApplyToBank = async (leadId) => {
    setProcessingAction(true);
    
    try {
      // You might want to call a specific API here to send the application to the bank
      // For now, we're updating the status to "Bank Application Submitted" (status code 9)
      await backofficeApi.updateCaseStatus(leadId, 9);
      
      // Update local state
      setLeads(leads.map(lead => 
        lead.id === leadId 
          ? { ...lead, status: "Bank Application Submitted", status_code: 9 } 
          : lead
      ));
      
      // Update selected lead if it's the one we're viewing
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ 
          ...selectedLead, 
          status: "Bank Application Submitted", 
          status_code: 9 
        });
      }
      
      // Show success message using SweetAlert2
      Swal.fire({
        title: 'Success!',
        text: 'Application pushed to bank successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        timer: 3000,
        timerProgressBar: true
      });
      
      // Refetch the leads to ensure we have the latest data
      fetchLeads();
    } catch (error) {
      console.error('Error applying to bank:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to push application to bank. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Handle Mark as Not Interested action
  const handleMarkNotInterested = async (leadId) => {
    setProcessingAction(true);
    
    try {
      // Call the API to update the status to "Not Interested" (code 14)
      await backofficeApi.updateCaseStatus(leadId, 14);
      
      // Update local state
      setLeads(leads.map(lead => 
        lead.id === leadId 
          ? { ...lead, status: "Not Interested", status_code: 14 } 
          : lead
      ));
      
      // Update selected lead if it's the one we're viewing
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ 
          ...selectedLead, 
          status: "Not Interested", 
          status_code: 14 
        });
      }
      
      // Show success message using SweetAlert2
      Swal.fire({
        title: 'Success!',
        text: 'Application marked as not interested',
        icon: 'success',
        confirmButtonText: 'OK',
        timer: 3000,
        timerProgressBar: true
      });
      
      // Refetch the leads to ensure we have the latest data
      fetchLeads();
    } catch (error) {
      console.error('Error marking as not interested:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update application status. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setProcessingAction(false);
    }
  };
  

  // Get list of unique products
  const products = ['All', ...new Set(leads.map(lead => lead.product))];

  return (
    <div className="backoffice-leads-container">
      <Row className="mb-3">
        <Col>
          <h4>Application Management</h4>
          <p className="text-muted">Process and manage customer applications</p>
        </Col>
      </Row>
      
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center g-3">
            <Col md={3} lg={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, mobile or agent..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            
            <Col md={3} lg={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FaFilter />
                </InputGroup.Text>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All Status</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
            
            <Col md={3} lg={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FaFilter />
                </InputGroup.Text>
                <Form.Select
                  value={productFilter}
                  onChange={(e) => setProductFilter(e.target.value)}
                >
                  {products.map((product, index) => (
                    <option key={index} value={product}>{product}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
                        
            <Col md={3} lg={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FaFilter />
                </InputGroup.Text>
                <Form.Select
                  value={searchedDsoId || "All"}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchedDsoId(value === "All" ? null : value);  // ✅ send null if All
                  }}
                >
                  <option value="All">All Dso</option>
                  {DsoFilter.map(dso => (
                    <option key={dso.id} value={dso.id}>
                      {dso.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>

          </Row>
        </Card.Body>
      </Card>
      
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading applications...</p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center p-5 text-muted">
              <FaExclamationTriangle size={28} className="mb-3" />
              <h5>No applications found</h5>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0 lead-table align-middle">
              <thead className="table-light text-muted">
                <tr>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>DSO</th>
                  <th>Agent</th>
                  <th>Profile</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td>{lead.name}</td>
                    <td>{lead.mobile}</td>
                    <td>{lead.product}</td>
                    <td>{renderStatusBadge(lead.status, lead.status_code)}</td>
                    <td>{lead.date}</td>
                    <td>{lead.dso}</td>
                    <td>{lead.agent}</td>
                    <td>
                      <ProgressBar
                        now={lead.completion}
                        variant={
                          lead.completion < 30
                            ? 'danger'
                            : lead.completion < 70
                            ? 'warning'
                            : 'success'
                        }
                        style={{ height: '10px', fontSize: '10px' }}
                        label={`${lead.completion}%`}
                      />
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewLead(lead)}
                          title="View Details"
                        >
                          <FaEye />
                        </Button>

                        {lead.status_code === 9 ||
                        lead.status_code === 10 ||
                        lead.status_code === 12 ||
                        lead.status_code === 13 ? (
                          <Button variant="outline-secondary" size="sm" disabled>
                            <FaCheck /> Bank
                          </Button>
                        ) : (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleApplyToBank(lead.id)}
                          >
                            <FaExternalLinkAlt /> Bank
                          </Button>
                        )}

                        {/* Uncomment if Not Interested feature is re-enabled
                        <Button
                          variant={lead.status_code === 14 ? "danger" : "outline-danger"}
                          size="sm"
                          onClick={() => handleMarkNotInterested(lead.id)}
                          title="Mark as Not Interested"
                          disabled={processingAction}
                        >
                          <FaTimes /> Not Int.
                        </Button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      
      {/* Lead Detail Modal */}
      <Modal show={showLeadModal} onHide={() => setShowLeadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          
          {selectedLead && (
            <Tabs defaultActiveKey="details" className="mb-3">
              {/* Tab 1: Customer Details */}
              <Tab eventKey="details" title="Customer Details">
                <Row>
                  <Col md={6}>
                    <label className="text-muted small">Customer Name</label>
                    <div className="fw-medium">{selectedLead?.lead_user?.name || "NA"}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Mobile Number</label>
                    <div className="fw-medium">{selectedLead?.lead_user?.phone || "NA"}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Gender</label>
                    <div className="fw-medium">{selectedLead?.lead_user?.gender || "NA"}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Date of Birth</label>
                    <div className="fw-medium">{selectedLead?.lead_user?.dob || "NA"}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Status</label>
                    <div>{renderStatusBadge(selectedLead.status, selectedLead.status_code)}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Product</label>
                    <div className="fw-medium">{selectedLead?.product?.name || "NA"}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Loan Amount</label>
                    <div className="fw-medium">₹{selectedLead?.loan_amount?.toLocaleString() || "NA"}</div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Created At</label>
                    <div className="fw-medium">{selectedLead?.created_at || "NA"}</div>
                  </Col>
                  <Col md={12}>
                    <label className="text-muted small">Customer Address</label>
                    <div className="fw-medium">
                      {selectedLead?.lead_user?.address}, {selectedLead?.lead_user?.city}, {selectedLead?.lead_user?.state} - {selectedLead?.lead_user?.pincode}
                    </div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Residence Type</label>
                    <div className="fw-medium">{selectedLead?.lead_user?.residence_type || "NA"}</div>
                  </Col>
                  <Col md={12}>
                    <label className="text-muted small">Office Address</label>
                    <div className="fw-medium">
                      {selectedLead?.lead_user?.office_address}, {selectedLead?.lead_user?.office_city}, {selectedLead?.lead_user?.office_state} - {selectedLead?.lead_user?.office_pincode}
                    </div>
                  </Col>
                  <Col md={6}>
                    <label className="text-muted small">Office Email</label>
                    <div className="fw-medium">{selectedLead?.lead_user?.office_email || "NA"}</div>
                  </Col>
                  <Col md={12}>
                    <label className="text-muted small">Profile Completion</label>
                    <ProgressBar 
                      now={selectedLead.completion} 
                      variant={
                        selectedLead.completion < 30 ? "danger" : 
                        selectedLead.completion < 70 ? "warning" : 
                        "success"
                      }
                      style={{ height: '10px' }}
                      label={`${selectedLead.completion}%`}
                    />
                  </Col>
                </Row>
              </Tab>

              {/* Tab 2: Documents */}
              <Tab eventKey="documents" title="Documents">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Document Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { type: "Identity Proof (Aadhaar)", key: "adhaar_card_file" },
                      { type: "PAN Card", key: "pan_card_file" },
                      { type: "Address Proof", key: "address_proof_file" },
                      { type: "Bank Statements", key: "bank_statements_file" },
                      { type: "Salary Slips", key: "salary_slips_file" },
                      { type: "Voter ID", key: "voter_id_card_file" }
                    ].map((doc, idx) => (
                      <tr key={idx}>
                        <td>{doc.type}</td>
                        <td>
                          <Badge bg={selectedLead?.lead_user?.[doc.key] ? "success" : "danger"}>
                            {selectedLead?.lead_user?.[doc.key] ? "Uploaded" : "Missing"}
                          </Badge>
                        </td>
                        <td>
                          {selectedLead?.lead_user?.[doc.key] ? (
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => window.open(selectedLead.lead_user[doc.key], "_blank")}
                            >
                              View
                            </Button>
                          ) : (
                            <Button variant="outline-secondary" size="sm" disabled>Not Available</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Tab>

              {/* Tab 3: Notes & Actions */}
              <Tab eventKey="notes" title="Notes & Actions">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Add Notes</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      value={leadNotes}
                      onChange={(e) => setLeadNotes(e.target.value)}
                      placeholder="Add notes about this lead..."
                    />
                  </Form.Group>

                  <Button 
                    variant="primary" 
                    disabled={!leadNotes.trim() || loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const response = await backofficeApi.updateCaseNotes(selectedLead.id, leadNotes);
                        if (response.data?.notes) {
                          setSavedNotes(prev => ({ ...prev, [selectedLead.id]: response.data.notes }));
                        } else {
                          setSavedNotes(prev => ({ ...prev, [selectedLead.id]: leadNotes }));
                        }
                        alert('Notes saved successfully!');
                      } catch (error) {
                        alert('Failed to save notes. Please try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : "Save Notes"}
                  </Button>
                </Form>

                {savedNotes[selectedLead?.id] && (
                  <div className="mt-3">
                    <h6>Saved Notes</h6>
                    <div className="p-3 bg-light rounded border">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{savedNotes[selectedLead.id]}</p>
                    </div>
                  </div>
                )}

                <hr />
                <div className="d-flex justify-content-center gap-2 mb-3">
                  {selectedLead.status_code === 9 || selectedLead.status_code === 10 || 
                  selectedLead.status_code === 12 || selectedLead.status_code === 13 ? (
                    <Button variant="outline-secondary" size="sm" disabled className="px-3">
                      <FaCheck className="me-1" /> Applied to Bank
                    </Button>
                  ) : (
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => {
                        handleApplyToBank(selectedLead.id);
                        setShowLeadModal(false);
                      }}
                      disabled={processingAction || selectedLead.status_code === 14}
                      className="px-3"
                    >
                      {processingAction ? <Spinner animation="border" size="sm" className="me-1" /> : <FaExternalLinkAlt className="me-1" />}
                      Apply to Bank
                    </Button>
                  )}

                  <Button 
                    variant={selectedLead.status_code === 14 ? "danger" : "outline-danger"}
                    size="sm"
                    onClick={() => {
                      handleMarkNotInterested(selectedLead.id);
                      setShowLeadModal(false);
                    }}
                    disabled={processingAction}
                    className="px-3"
                  >
                    {processingAction ? <Spinner animation="border" size="sm" className="me-1" /> : <FaTimes className="me-1" />}
                    Not Interested
                  </Button>
                </div>

                <div className="alert alert-warning">
                  <div className="fw-bold">Important:</div>
                  <ul className="mb-0 ps-3">
                    <li>Complete KYC verification before processing</li>
                    <li>Verify all documents for authenticity</li>
                    <li>Check customer credit score</li>
                    <li>Document all communication with the customer</li>
                  </ul>
                </div>
              </Tab>
            </Tabs>


          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLeadModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      
      <style jsx>{`

        :global(.dropdown-menu) {
          z-index: 1050 !important;
        }

        :global(.card) {
          overflow: visible !important;
        }

        :global(.table-responsive) {
          overflow: visible !important;
        }

        :global(.table) {
          overflow: visible !important;
          position: relative;
        }

        .table td {
          position: relative;
          overflow: visible;
        }
        
        .dropdown-menu {
          z-index: 2000 !important;
        }

        .card, .table-responsive, .table {
          overflow: visible !important;
        }
        
        /* Timeline styles */
        .timeline {
          position: relative;
          padding: 1rem 0;
        }
        
        .timeline::before {
          content: '';
          position: absolute;
          top: 0;
          left: 12px;
          height: 100%;
          width: 2px;
          background: #e2e8f0;
        }
        
        .timeline-item {
          position: relative;
          padding-left: 40px;
          padding-bottom: 1.5rem;
        }
        
        .timeline-marker {
          position: absolute;
          left: 0;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 0 0 2px #e2e8f0;
        }
        
        .timeline-content {
          padding: 0.5rem 0;
        }
      `}</style>


    <div className="flex justify-center items-center space-x-2 mt-6">
      {/* Prev Button */}
      <button
        className="px-3 py-1.5 rounded-lg border text-sm font-medium transition 
                  bg-white text-gray-700 hover:bg-gray-100 
                  disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={page === 1}
        onClick={() => setPage(prev => prev - 1)}
      >
        ← Prev
      </button>

      {/* Page Numbers */}
      {[...Array(Math.ceil(total / limit))].map((_, i) => {
        const pageNum = i + 1;

        if (
          pageNum === 1 ||
          pageNum === Math.ceil(total / limit) ||
          (pageNum >= page - 1 && pageNum <= page + 1)
        ) {
          return (
            <button
              key={pageNum}
              onClick={() => setPage(pageNum)}
              className={`w-9 h-9 rounded-lg border text-sm font-medium transition 
                ${
                  page === pageNum
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
            >
              {pageNum}
            </button>
          );
        } else if (pageNum === page - 2 || pageNum === page + 2) {
          return (
            <span key={pageNum} className="px-2 text-gray-400">
              …
            </span>
          );
        }
        return null;
      })}

      {/* Next Button */}
      <button
        className="px-3 py-1.5 rounded-lg border text-sm font-medium transition 
                  bg-white text-gray-700 hover:bg-gray-100 
                  disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={page >= Math.ceil(total / limit)}
        onClick={() => setPage(prev => prev + 1)}
      >
        Next →
      </button>
    </div>




    </div>
  );
};

export default BackofficeLeads;